// Tranche CC partial: pre-launch integration audit page.
//
// Aggregates the small set of "is this install actually ready for
// real users?" signals into a single readable checklist. Master-only.
// Pure aggregation of existing endpoints — no new backend surface.
//
// Each check resolves to one of:
//   - ok       (✅): the install is in the right shape for this concern
//   - warn     (⚠️): partial / soft failure / known-not-acceptable for prod
//   - fail     (❌): hard blocker; the operator must resolve before go-live
//   - unknown  (· ): the endpoint did not answer; the row stays visible
//                    so the operator knows the check didn't run
//
// Out of scope for this PR (documented at bottom of the page):
//   - Live-controller-verified emergency / NFC / door-unlock matrix rows
//     (those are doc-only on the matrix page; we link instead of
//     duplicating their state here).
//   - Secret rotation freshness — no portal endpoint surfaces last
//     rotated_at; operator manually verifies.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Card, CardBody, Icon, Spinner } from '@ng-unifi/ui';
import { apiClient, ApiError } from '../lib/api.js';
import { useAuth } from '../auth/AuthContext.js';
import { siteModeLabel } from '../lib/humanLabels.js';

type CheckStatus = 'ok' | 'warn' | 'fail' | 'unknown';

interface Check {
  id: string;
  title: string;
  status: CheckStatus;
  detail: string;
  remediation: { label: string; href: string } | null;
}

interface SetupStatus {
  completed: boolean;
  has_master: boolean;
  has_unifi: boolean;
  mfa_enforced: boolean;
  site_mode: 'commercial_multi_tenant' | 'single_tenant' | 'multi_family';
}

interface WebhookHealth {
  last_event_at: string | null;
  events_last_hour: number;
  events_last_24h: number;
  webhook_callback_url: string;
  registration_status: 'registered' | 'failed' | 'not_attempted' | 'unknown';
  controller_verified: boolean | null;
}

interface GrantRow {
  id: string;
  tenant_id: string;
}

interface TenantRow {
  id: string;
  name: string;
  status: 'active' | 'suspended' | 'archived';
}

function toneOf(s: CheckStatus): 'success' | 'warning' | 'danger' | 'neutral' {
  if (s === 'ok') return 'success';
  if (s === 'warn') return 'warning';
  if (s === 'fail') return 'danger';
  return 'neutral';
}

function iconOf(s: CheckStatus): string {
  if (s === 'ok') return 'check_circle';
  if (s === 'warn') return 'warning';
  if (s === 'fail') return 'error';
  return 'help';
}

interface Aggregated {
  checks: Check[];
  /** Number of unknown checks — surfaced as a separate banner. */
  unknownCount: number;
}

async function loadAggregated(defaultTenantHealth: string | null): Promise<Aggregated> {
  // All loads are best-effort. A 403 from any endpoint surfaces as
  // `unknown` rather than failing the whole page, because a tenant-
  // admin who deep-links here should still see what they can.
  const safeGet = async <T,>(url: string): Promise<T | null> => {
    try {
      const res = await apiClient.get<{ data: T }>(url, { skipTenantScope: true });
      return res.data;
    } catch {
      return null;
    }
  };

  const [setup, webhook, grants, tenants] = await Promise.all([
    safeGet<SetupStatus>('/api/v1/setup/status'),
    safeGet<WebhookHealth>('/api/v1/admin/webhooks/health'),
    safeGet<GrantRow[]>('/api/v1/admin/hardware/grants'),
    safeGet<TenantRow[]>('/api/v1/tenants'),
  ]);

  const checks: Check[] = [];

  // 1. Setup completed (foundation)
  if (setup === null) {
    checks.push({
      id: 'setup-completed',
      title: 'Setup wizard completed',
      status: 'unknown',
      detail: '/api/v1/setup/status did not answer — cannot verify.',
      remediation: null,
    });
  } else if (setup.completed && setup.has_master && setup.has_unifi) {
    checks.push({
      id: 'setup-completed',
      title: 'Setup wizard completed',
      status: 'ok',
      detail: `Site mode: ${siteModeLabel(setup.site_mode)}. Master admin present, controller URL set.`,
      remediation: null,
    });
  } else {
    checks.push({
      id: 'setup-completed',
      title: 'Setup wizard completed',
      status: 'fail',
      detail: setup.completed
        ? 'Setup flag set but master admin or controller config is missing.'
        : 'Install has not run /setup/init yet.',
      remediation: { label: 'Open setup wizard', href: '/setup' },
    });
  }

  // 2. MFA enforced
  if (setup === null) {
    checks.push({
      id: 'mfa-enforced',
      title: 'MFA enforced for master/admin',
      status: 'unknown',
      detail: '/api/v1/setup/status did not answer — cannot verify.',
      remediation: null,
    });
  } else if (setup.mfa_enforced) {
    checks.push({
      id: 'mfa-enforced',
      title: 'MFA enforced for master/admin',
      status: 'ok',
      detail: 'MFA_ENFORCED=true. Privileged accounts must enroll a passkey or authenticator fallback before login.',
      remediation: null,
    });
  } else {
    checks.push({
      id: 'mfa-enforced',
      title: 'MFA enforced for master/admin',
      status: 'fail',
      detail: 'MFA_ENFORCED=false. Privileged accounts can sign in without a second factor — not acceptable for production.',
      remediation: {
        label: 'How to enable MFA',
        href: '/admin/settings/default-tenant',
      },
    });
  }

  // 3. Default tenant healthy (single_tenant + multi_family only)
  if (setup && (setup.site_mode === 'single_tenant' || setup.site_mode === 'multi_family')) {
    const dth = defaultTenantHealth ?? 'unknown';
    if (dth === 'ok') {
      checks.push({
        id: 'default-tenant',
        title: 'Default tenant pinned and active',
        status: 'ok',
        detail: 'site.default_tenant_id resolves to an active tenant.',
        remediation: null,
      });
    } else if (dth === 'unknown') {
      checks.push({
        id: 'default-tenant',
        title: 'Default tenant pinned and active',
        status: 'unknown',
        detail: '/api/v1/auth/me did not return default_tenant_health.',
        remediation: null,
      });
    } else {
      checks.push({
        id: 'default-tenant',
        title: 'Default tenant pinned and active',
        status: 'fail',
        detail: `default_tenant_health = "${dth}". Every tenant-scoped page will refuse to load until repaired.`,
        remediation: {
          label: 'Repair default tenant',
          href: '/admin/settings/default-tenant',
        },
      });
    }
  }

  // 4. Webhook registration + delivery
  if (webhook === null) {
    checks.push({
      id: 'webhook-registration',
      title: 'Controller webhook registered',
      status: 'unknown',
      detail: '/api/v1/admin/webhooks/health did not answer.',
      remediation: null,
    });
  } else if (webhook.registration_status === 'registered' && webhook.controller_verified === true) {
    checks.push({
      id: 'webhook-registration',
      title: 'Controller webhook registered',
      status: 'ok',
      detail: `Subscription registered + verified against the controller. ${webhook.events_last_24h} events delivered in last 24h.`,
      remediation: null,
    });
  } else if (webhook.registration_status === 'registered' && webhook.events_last_24h === 0) {
    checks.push({
      id: 'webhook-registration',
      title: 'Controller webhook registered',
      status: 'warn',
      detail: 'Registration recorded but no events delivered in the last 24h. Either the controller is silent, the signature is rejecting, or registration is stale.',
      remediation: { label: 'Inspect webhook health', href: '/admin/webhooks' },
    });
  } else if (webhook.registration_status === 'failed') {
    checks.push({
      id: 'webhook-registration',
      title: 'Controller webhook registered',
      status: 'fail',
      detail: 'Setup attempted registration but the controller rejected. /api/v1/events will stay empty until repaired.',
      remediation: { label: 'Re-register webhook', href: '/admin/webhooks' },
    });
  } else {
    checks.push({
      id: 'webhook-registration',
      title: 'Controller webhook registered',
      status: 'fail',
      detail: 'No registration attempted. The portal receiver is wired but the controller has no destination to POST to.',
      remediation: { label: 'Re-register webhook', href: '/admin/webhooks' },
    });
  }

  // 5. Door permissions granted (at least one tenant resource grant)
  if (grants === null) {
    checks.push({
      id: 'door-grants',
      title: 'Doors granted for at least one tenant',
      status: 'unknown',
      detail: '/api/v1/admin/hardware/grants did not answer.',
      remediation: null,
    });
  } else if (grants.length === 0) {
    checks.push({
      id: 'door-grants',
      title: 'Doors granted for at least one tenant',
      status: 'fail',
      detail: 'No tenant resource grants. No tenant can lock or unlock any door.',
      remediation: { label: 'Open door permissions', href: '/hardware/claim' },
    });
  } else {
    checks.push({
      id: 'door-grants',
      title: 'Doors granted for at least one tenant',
      status: 'ok',
      detail: `${grants.length} resource grant${grants.length === 1 ? '' : 's'} across the install.`,
      remediation: null,
    });
  }

  // 6. Tenants (commercial mode only — single/multifamily have one auto-tenant)
  if (setup?.site_mode === 'commercial_multi_tenant') {
    if (tenants === null) {
      checks.push({
        id: 'tenants',
        title: 'At least one active tenant exists',
        status: 'unknown',
        detail: '/api/v1/tenants did not answer.',
        remediation: null,
      });
    } else {
      const active = tenants.filter((t) => t.status === 'active');
      if (active.length === 0) {
        checks.push({
          id: 'tenants',
          title: 'At least one active tenant exists',
          status: 'fail',
          detail: `${tenants.length} tenant row${tenants.length === 1 ? '' : 's'} but none are active. Create or restore one before go-live.`,
          remediation: { label: 'Manage tenants', href: '/tenants' },
        });
      } else {
        checks.push({
          id: 'tenants',
          title: 'At least one active tenant exists',
          status: 'ok',
          detail: `${active.length} active / ${tenants.length} total.`,
          remediation: null,
        });
      }
    }
  }

  // 7. Live UniFi verification — link out; we cannot infer state from
  // the matrix doc client-side.
  checks.push({
    id: 'live-verification',
    title: 'UniFi compatibility matrix flipped',
    status: 'unknown',
    detail: 'Live-verification state lives in docs/architecture/UNIFI_COMPATIBILITY_MATRIX.md and is not exposed via the API. Cross-check manually before go-live.',
    remediation: null,
  });

  const unknownCount = checks.filter((c) => c.status === 'unknown').length;
  return { checks, unknownCount };
}

export function PreLaunchAuditPage() {
  const { state } = useAuth();
  const [agg, setAgg] = useState<Aggregated | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  const defaultTenantHealth =
    state.status === 'authed' ? state.me.default_tenant_health : null;

  useEffect(() => {
    let cancelled = false;
    setAgg(null);
    setError(null);
    loadAggregated(defaultTenantHealth)
      .then((r) => {
        if (cancelled) return;
        setAgg(r);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Failed to run audit');
      });
    return () => {
      cancelled = true;
    };
  }, [reloadTick, defaultTenantHealth]);

  const counts = (() => {
    const c = { ok: 0, warn: 0, fail: 0, unknown: 0 };
    for (const x of agg?.checks ?? []) c[x.status] += 1;
    return c;
  })();

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <span className="rounded-full bg-secondary-container p-2 text-on-secondary-container">
            <Icon name="checklist" />
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="font-headline-sm text-headline-sm text-on-surface">
              Pre-launch audit
            </h1>
            <p className="font-body-md text-body-md text-on-surface-variant">
              Go-live readiness checks aggregated from the portal's existing
              health endpoints. Run before promoting to production users.
            </p>
          </div>
          <Button variant="secondary" onClick={() => setReloadTick((t) => t + 1)}>
            <Icon name="refresh" className="text-sm" />
            <span className="ml-1">Re-run</span>
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge tone="success">{counts.ok} ok</Badge>
          <Badge tone={counts.warn > 0 ? 'warning' : 'neutral'}>{counts.warn} warn</Badge>
          <Badge tone={counts.fail > 0 ? 'danger' : 'neutral'}>{counts.fail} blocker{counts.fail === 1 ? '' : 's'}</Badge>
          <Badge>{counts.unknown} unknown</Badge>
        </div>
      </header>

      {error && (
        <Card>
          <CardBody>
            <p className="font-body-md text-body-md text-error" role="alert">
              {error}
            </p>
          </CardBody>
        </Card>
      )}

      {agg === null && !error && (
        <div className="flex items-center gap-3 text-on-surface-variant">
          <Spinner />
          <span>Running checks…</span>
        </div>
      )}

      {agg !== null && counts.fail > 0 && (
        <Card>
          <CardBody>
            <p className="font-title-md text-title-md text-on-surface mb-1">
              Not ready for go-live
            </p>
            <p className="font-body-md text-body-md text-on-surface-variant">
              {counts.fail} blocker{counts.fail === 1 ? '' : 's'} below. Each row
              has a remediation link.
            </p>
          </CardBody>
        </Card>
      )}

      {agg !== null && counts.fail === 0 && counts.warn === 0 && counts.unknown === 0 && (
        <Card>
          <CardBody>
            <p className="font-title-md text-title-md text-on-surface mb-1">
              All checks clear
            </p>
            <p className="font-body-md text-body-md text-on-surface-variant">
              The install passes every aggregated readiness check. Operator
              should still cross-check the live-verification matrix and the
              secret rotation log before promoting to real users.
            </p>
          </CardBody>
        </Card>
      )}

      {agg !== null && (
        <ul className="space-y-3">
          {agg.checks.map((c) => (
            <li key={c.id}>
              <Card>
                <CardBody>
                  <div className="flex items-start gap-3">
                    <Badge tone={toneOf(c.status)}>
                      <Icon name={iconOf(c.status)} className="text-sm" />
                      <span className="ml-1 uppercase">{c.status}</span>
                    </Badge>
                    <div className="min-w-0 flex-1">
                      <h2 className="font-title-md text-title-md text-on-surface">
                        {c.title}
                      </h2>
                      <p className="break-words font-body-md text-body-md text-on-surface-variant">
                        {c.detail}
                      </p>
                      {c.remediation && (
                        <Link
                          to={c.remediation.href}
                          className="mt-2 inline-flex min-h-[1.5rem] items-center gap-1 py-1 font-label-md text-label-md text-primary hover:underline"
                        >
                          {c.remediation.label} →
                        </Link>
                      )}
                    </div>
                  </div>
                </CardBody>
              </Card>
            </li>
          ))}
        </ul>
      )}

      <Card>
        <CardBody>
          <h2 className="font-title-md text-title-md text-on-surface mb-2">
            Manual checks (not automated)
          </h2>
          <ul className="list-disc pl-6 font-body-md text-body-md text-on-surface space-y-1">
            <li>
              Live UniFi compatibility matrix flipped for the target controller +
              firmware. See{' '}
              <code className="break-all">docs/architecture/UNIFI_COMPATIBILITY_MATRIX.md</code>.
            </li>
            <li>
              Secret rotation: <code className="break-all">JWT_SECRET</code>,{' '}
              <code className="break-all">APP_SECRETS_KEY</code>, <code className="break-all">WEBHOOK_HMAC_SECRET</code>,
              and the UniFi API key rotated in the last 90 days.
            </li>
            <li>
              Backup + restore drill rehearsed against the elkridge LXC. See{' '}
              <code className="break-all">docs/runbooks/backup-restore.md</code>.
            </li>
            <li>
              Production reverse-proxy CSP + CORS verified against the
              deployed origin.
            </li>
          </ul>
        </CardBody>
      </Card>
    </div>
  );
}
